"use client"

import * as React from "react"
import { createPortal } from "react-dom"
import { useCreateProject } from "@/hooks/use-projects"
import { useSearchUsers } from "@/hooks/use-users"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { Plus, X, Check, Upload, Search, ChevronRight, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { EmojiPickerModal } from "./emoji-picker-modal"
import { UserAvatar } from "@/components/ui/user-avatar"
import { DatePicker } from "@/components/ui/date-picker"

// Wizard steps
type WizardStep = 'details' | 'upload' | 'processing'

// Processing status items
interface ProcessingItem {
  id: string
  label: string
  status: 'pending' | 'active' | 'completed'
}

// Collaborator type
interface Collaborator {
  id: string
  name: string
  email: string
}

// Emoji options removed - now using EmojiPickerModal with categories

interface CreateProjectDialogProps {
  trigger?: React.ReactNode
}

export function CreateProjectDialog({ trigger }: CreateProjectDialogProps) {
  const [open, setOpen] = React.useState(false)
  const { toast } = useToast()
  const createProject = useCreateProject()

  // Wizard state
  const [currentStep, setCurrentStep] = React.useState<WizardStep>('details')
  
  // Details step state
  const [projectName, setProjectName] = React.useState('')
  const [projectDescription, setProjectDescription] = React.useState('')
  const [deadline, setDeadline] = React.useState('')
  const [selectedEmoji, setSelectedEmoji] = React.useState('📁')
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false)
  const [collaboratorSearch, setCollaboratorSearch] = React.useState('')
  const [selectedCollaborators, setSelectedCollaborators] = React.useState<Collaborator[]>([])
  const [showCollaboratorDropdown, setShowCollaboratorDropdown] = React.useState(false)
  const collaboratorInputRef = React.useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 })

  // Update dropdown position when it opens
  React.useEffect(() => {
    if (showCollaboratorDropdown && collaboratorInputRef.current) {
      const rect = collaboratorInputRef.current.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      })
    }
  }, [showCollaboratorDropdown, collaboratorSearch])

  // Upload step state
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>([])
  const [isDragging, setIsDragging] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Processing step state
  const [processingProgress, setProcessingProgress] = React.useState(0)
  const [processingItems, setProcessingItems] = React.useState<ProcessingItem[]>([
    { id: '1', label: 'Extracting text from documents...', status: 'pending' },
    { id: '2', label: 'Generating embeddings...', status: 'pending' },
    { id: '3', label: 'Creating knowledge base...', status: 'pending' },
    { id: '4', label: 'Running multi-agent analysis...', status: 'pending' },
    { id: '5', label: 'Generating artifacts...', status: 'pending' },
  ])

  // Use real user search API
  const { data: userSearchResults, isLoading: isSearching } = useSearchUsers(collaboratorSearch)

  // Filter out already selected collaborators from search results
  const filteredCollaborators = React.useMemo(() => {
    if (!userSearchResults?.users) return []
    return userSearchResults.users
      .filter((u) => !selectedCollaborators.find((s) => s.id === u.id))
      .map((u) => ({
        id: u.id,
        name: u.name || u.email.split('@')[0],
        email: u.email,
      }))
  }, [userSearchResults, selectedCollaborators])

  // Reset wizard when dialog closes
  const handleClose = () => {
    setOpen(false)
    setTimeout(() => {
      setCurrentStep('details')
      setProjectName('')
      setProjectDescription('')
      setDeadline('')
      setSelectedEmoji('📁')
      setSelectedCollaborators([])
      setCollaboratorSearch('')
      setUploadedFiles([])
      setProcessingProgress(0)
      setProcessingItems((items) =>
        items.map((item) => ({ ...item, status: 'pending' as const }))
      )
    }, 200)
  }

  // Handle file drop
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    setUploadedFiles((prev) => [...prev, ...files])
  }

  // Handle file select
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files)
      setUploadedFiles((prev) => [...prev, ...files])
    }
  }

  // Remove uploaded file
  const removeFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  // Add collaborator
  const addCollaborator = (collaborator: Collaborator) => {
    setSelectedCollaborators((prev) => [...prev, collaborator])
    setCollaboratorSearch('')
    setShowCollaboratorDropdown(false)
  }

  // Remove collaborator
  const removeCollaborator = (id: string) => {
    setSelectedCollaborators((prev) => prev.filter((c) => c.id !== id))
  }

  // Go to next step
  const handleNext = () => {
    if (currentStep === 'details') {
      setCurrentStep('upload')
    }
  }

  // Helper to set processing step status
  const setStepStatus = (stepIndex: number, status: 'pending' | 'active' | 'completed') => {
    setProcessingItems((prev) =>
      prev.map((item, idx) => ({
        ...item,
        status: idx === stepIndex ? status : idx < stepIndex ? 'completed' : 'pending',
      }))
    )
    setProcessingProgress(((stepIndex + (status === 'completed' ? 1 : 0.5)) / 5) * 100)
  }

  // Start processing
  const handleCreateProject = async () => {
    setCurrentStep('processing')
    setProcessingProgress(0)
    setProcessingItems((items) => items.map((item) => ({ ...item, status: 'pending' as const })))

    let createdProjectId: string | null = null

    try {
      // Step 1: Extracting text (simulated for files)
      setStepStatus(0, 'active')
      if (uploadedFiles.length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 600))
      }
      setStepStatus(0, 'completed')

      // Step 2: Generating embeddings
      setStepStatus(1, 'active')
      await new Promise((resolve) => setTimeout(resolve, 500))
      setStepStatus(1, 'completed')

      // Step 3: Creating knowledge base - this is where we create the project
      setStepStatus(2, 'active')
      
      const project = await createProject.mutateAsync({
        name: projectName,
        type: 'enterprise',
        description: projectDescription,
        deadline: deadline,
        emoji: selectedEmoji,
        collaboratorIds: selectedCollaborators.map(c => c.id),
      })
      createdProjectId = project.id
      setStepStatus(2, 'completed')

      // Step 4: Running multi-agent analysis + uploading files
      setStepStatus(3, 'active')
      
      // Upload files if any
      if (uploadedFiles.length > 0 && createdProjectId) {
        const token = localStorage.getItem('auth_token')
        for (const file of uploadedFiles) {
          const formData = new FormData()
          formData.append('file', file)
          
          try {
            await fetch(`/api/projects/${createdProjectId}/documents`, {
              method: 'POST',
              headers: {
                ...(token && { Authorization: `Bearer ${token}` }),
              },
              body: formData,
            })
          } catch (err) {
            console.error('Failed to upload file:', file.name, err)
          }
        }
      }
      setStepStatus(3, 'completed')

      // Step 5: Generating artifacts
      setStepStatus(4, 'active')
      await new Promise((resolve) => setTimeout(resolve, 500))
      setStepStatus(4, 'completed')

      toast({
        title: "Project created!",
        description: `"${projectName}" has been created successfully.`,
        variant: "success",
      })
      
      // Navigate to the new project after a short delay
      setTimeout(() => {
        handleClose()
        if (createdProjectId) {
          window.location.href = `/projects/${createdProjectId}`
        }
      }, 1000)
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create project. Please try again.",
        variant: "destructive",
      })
      setCurrentStep('details')
    }
  }

  // Check if can proceed to next step
  const canProceed = projectName.trim().length >= 3

  if (!open) {
    return (
      <div onClick={() => setOpen(true)}>
        {trigger || (
          <Button className="h-10 px-4 flex items-center gap-2 bg-brand-primary rounded-[10px] hover:bg-brand-primary/90">
            <Plus className="w-5 h-5" />
            <span className="text-base">New Project</span>
          </Button>
        )}
      </div>
    )
  }

  const dialogContent = (
    <div className="fixed inset-0 z-50 p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Dialog */}
      <div className="relative z-10 mx-auto my-auto flex min-h-full items-center justify-center">
        <div className="relative bg-white rounded-[14px] shadow-xl w-full max-w-[550px] max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-medium text-gray-900">Create New Project</h2>
          <button
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center rounded-[10px] hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 pb-4">
          <div className="flex items-center justify-between">
            {/* Step 1: Details */}
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium",
                currentStep === 'details' 
                  ? "bg-brand-primary text-white"
                  : "bg-green-500 text-white"
              )}>
                {currentStep === 'details' ? '1' : <Check className="w-4 h-4" />}
              </div>
              <span className={cn(
                "text-sm font-medium",
                currentStep === 'details' ? "text-gray-900" : "text-gray-600"
              )}>Details</span>
            </div>

            {/* Connector */}
            <div className={cn(
              "flex-1 h-0.5 mx-3",
              currentStep !== 'details' ? "bg-green-500" : "bg-gray-200"
            )} />

            {/* Step 2: Upload */}
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium",
                currentStep === 'upload' 
                  ? "bg-brand-primary text-white"
                  : currentStep === 'processing'
                    ? "bg-green-500 text-white"
                    : "bg-gray-200 text-gray-600"
              )}>
                {currentStep === 'processing' ? <Check className="w-4 h-4" /> : '2'}
              </div>
              <span className={cn(
                "text-sm font-medium",
                currentStep === 'upload' ? "text-gray-900" : "text-gray-600"
              )}>Upload</span>
            </div>

            {/* Connector */}
            <div className={cn(
              "flex-1 h-0.5 mx-3",
              currentStep === 'processing' ? "bg-green-500" : "bg-gray-200"
            )} />

            {/* Step 3: Processing */}
            <div className="flex items-center gap-2">
              <div className={cn(
                "w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium",
                currentStep === 'processing' 
                  ? "bg-brand-primary text-white"
                  : "bg-gray-200 text-gray-600"
              )}>
                3
              </div>
              <span className={cn(
                "text-sm font-medium",
                currentStep === 'processing' ? "text-gray-900" : "text-gray-600"
              )}>Processing</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-6 overflow-y-auto flex-1">
          {/* Step 1: Details */}
          {currentStep === 'details' && (
            <div className="space-y-5">
              {/* Icon and Project Name */}
              <div className="flex items-start gap-4">
                {/* Icon Selector */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Icon
                  </label>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="w-14 h-14 bg-gray-100 rounded-[10px] flex items-center justify-center hover:bg-gray-200 transition-colors border border-gray-200"
                  >
                    <span className="text-2xl">{selectedEmoji}</span>
                  </button>
                  
                  {/* Emoji Picker Modal rendered at the end of component */}
                </div>

                {/* Project Name */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Name
                  </label>
                  <input
                    type="text"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    placeholder="Enter project name..."
                    className="w-full h-[42px] px-4 border border-gray-300 rounded-[10px] bg-white text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  />
                </div>
              </div>

              {/* Project Context / Company Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Project Context / Company Description
                </label>
                <textarea
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  placeholder="Provide context about the project, company background, target audience, or business goals..."
                  className="w-full h-[100px] p-4 border border-gray-300 rounded-[10px] bg-white text-base text-gray-900 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                />
              </div>

              {/* Deadline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deadline
                </label>
                <DatePicker
                  value={deadline}
                  onChange={(date) => {
                    if (!date) {
                      setDeadline('');
                      return;
                    }
                    // Format as YYYY-MM-DD using local date (not UTC)
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    setDeadline(`${year}-${month}-${day}`);
                  }}
                  placeholder="Select project deadline"
                  minDate={new Date()}
                />
              </div>

              {/* Collaborators */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Collaborators
                </label>
                <div className="relative" ref={collaboratorInputRef}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={collaboratorSearch}
                    onChange={(e) => {
                      setCollaboratorSearch(e.target.value)
                      setShowCollaboratorDropdown(true)
                    }}
                    onFocus={() => setShowCollaboratorDropdown(true)}
                    onBlur={() => {
                      // Delay hiding to allow click on dropdown items
                      setTimeout(() => setShowCollaboratorDropdown(false), 200)
                    }}
                    placeholder="Search and add collaborators..."
                    className="w-full h-[42px] pl-10 pr-4 border border-gray-300 rounded-[10px] bg-white text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary"
                  />
                  
                  {/* Collaborator dropdown - rendered via Portal */}
                  {showCollaboratorDropdown && collaboratorSearch.length >= 2 && typeof document !== 'undefined' && createPortal(
                    <div 
                      className="fixed bg-white border border-gray-200 rounded-[10px] shadow-xl max-h-48 overflow-auto py-1"
                      style={{
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width,
                        zIndex: 9999,
                      }}
                    >
                      {isSearching ? (
                        <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Searching users...
                        </div>
                      ) : filteredCollaborators.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          No users found
                        </div>
                      ) : (
                        filteredCollaborators.map((collaborator) => (
                          <button
                            key={collaborator.id}
                            onMouseDown={(e) => {
                              e.preventDefault() // Prevent blur before click
                              addCollaborator(collaborator)
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-3"
                          >
                            <UserAvatar 
                              name={collaborator.name} 
                              email={collaborator.email}
                              size="sm"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-900">{collaborator.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">{collaborator.email}</div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>,
                    document.body
                  )}
                </div>

                {/* Selected collaborators */}
                {selectedCollaborators.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {selectedCollaborators.map((collaborator) => (
                      <div
                        key={collaborator.id}
                        className="flex items-center gap-2 pl-1 pr-3 py-1 bg-gray-50 border border-gray-200 rounded-xl"
                      >
                        <UserAvatar 
                          name={collaborator.name} 
                          email={collaborator.email}
                          size="sm"
                        />
                        <span className="text-sm text-gray-700">{collaborator.name}</span>
                        <button
                          onClick={() => removeCollaborator(collaborator.id)}
                          className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Upload */}
          {currentStep === 'upload' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Upload project materials to build your knowledge base
              </p>

              {/* Drop Zone */}
              <div
                onDragOver={(e) => {
                  e.preventDefault()
                  setIsDragging(true)
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={cn(
                  "border-2 border-dashed rounded-[14px] p-8 text-center transition-colors",
                  isDragging
                    ? "border-brand-primary bg-brand-primary/5"
                    : "border-gray-300 hover:border-gray-400"
                )}
              >
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-base text-gray-700 mb-1">Drag and drop files here</p>
                <p className="text-sm text-gray-500 mb-4">or click to browse</p>
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-brand-primary hover:bg-brand-primary/90 rounded-[10px]"
                >
                  Select Files
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Uploaded Files */}
              {uploadedFiles.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Uploaded Files ({uploadedFiles.length})
                  </p>
                  <div className="space-y-2">
                    {uploadedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between px-4 py-2 bg-gray-50 rounded-[10px]"
                      >
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <button
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Processing */}
          {currentStep === 'processing' && (
            <div className="py-4">
              {/* Spinner */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 border-4 border-gray-200 border-t-brand-primary rounded-full animate-spin" />
              </div>

              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Processing Your Project
              </h3>
              <p className="text-sm text-gray-600 text-center mb-6">
                AI agents are analyzing documents and generating artifacts...
              </p>

              {/* Progress Bar */}
              <div className="mb-2">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-primary transition-all duration-500"
                    style={{ width: `${processingProgress}%` }}
                  />
                </div>
              </div>
              <p className="text-sm text-gray-600 text-center mb-6">
                {Math.round(processingProgress)}% complete
              </p>

              {/* Processing Items */}
              <div className="space-y-3">
                {processingItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    {item.status === 'completed' ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : item.status === 'active' ? (
                      <div className="w-5 h-5 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                    )}
                    <span className={cn(
                      "text-sm",
                      item.status === 'completed' ? "text-green-600" :
                      item.status === 'active' ? "text-gray-900" :
                      "text-gray-400"
                    )}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {currentStep !== 'processing' && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={handleClose}
              className="text-base text-gray-700 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            
            {currentStep === 'details' ? (
              <Button
                onClick={handleNext}
                disabled={!canProceed}
                className={cn(
                  "h-10 px-5 rounded-[10px] flex items-center gap-2",
                  canProceed
                    ? "bg-brand-primary text-white hover:bg-brand-primary/90"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                )}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={handleCreateProject}
                className="h-10 px-5 rounded-[10px] bg-[#2dd4bf] text-white hover:bg-[#2dd4bf]/90 flex items-center gap-2"
              >
                Create Project
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Emoji Picker Modal */}
      <EmojiPickerModal
        isOpen={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        onSelect={(emoji) => setSelectedEmoji(emoji)}
        currentEmoji={selectedEmoji}
      />
    </div>
  )

  if (typeof document !== 'undefined') {
    return createPortal(dialogContent, document.body)
  }

  return dialogContent
}
